import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IHasSourceIdentifier,
  IMigratedDataSummary,
  ISourceEntityHandler,
  ISourceEntityTypes,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ISourceEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ClinicalNote,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  IArraySorter,
  type IClinicalNote,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  PrimaryKey,
  WithPrimaryKey,
  resolveSequentially,
  getError,
  safeCombineLatest,
  toTimestamp,
  type WithRef,
  FirestoreMigrate,
  INamedDocument,
  Transaction,
  runTransaction,
} from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ItemCodeResourceMapType } from '../../mappings/item-codes-to-xlsx';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { PatientIdFilter } from '../filters/patient-id-filter';
import {
  PATIENT_CLINICAL_NOTE_CUSTOM_MAPPING_TYPE,
  PATIENT_CLINICAL_NOTE_DESTINATION_ENTITY,
} from './consts';

interface IClinicalNoteSuccessData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  clinicalNoteRefs: DocumentReference<IClinicalNote>[];
}

export interface IClinicalNoteJobData<
  PatientRecord extends ISourceEntityTypes,
> {
  sourcePatient: IGetRecordResponse<PatientRecord>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  practitioners: INamedDocument<IStaffer>[];
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
}

export interface IClinicalNoteMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  clinicalNotes: (IClinicalNote & IHasSourceIdentifier)[];
}

export abstract class BasePatientClinicalNoteDestinationEntity<
  PatientRecord extends ISourceEntityTypes,
  JobData extends
    IClinicalNoteJobData<PatientRecord> = IClinicalNoteJobData<PatientRecord>,
> extends BaseDestinationEntity<
  IClinicalNoteSuccessData,
  JobData,
  IClinicalNoteMigrationData
> {
  destinationEntity = PATIENT_CLINICAL_NOTE_DESTINATION_ENTITY;
  abstract patientSourceEntity: ISourceEntityHandler<PatientRecord>;
  override canMigrateByIdRange = true;

  get sourceCountComparison(): ISourceEntityHandler<PatientRecord> {
    return this.patientSourceEntity;
  }

  override filters = [
    new PatientIdFilter<JobData>((jobData) =>
      this.patientSourceEntity
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  override sorters: IArraySorter[] = [
    {
      key: 'clinicalNotes',
      sortByPath: 'createdAt.seconds',
    },
  ];

  sourceCountDataAccessor(data: JobData): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: JobData): string {
    return data.sourcePatient.record.uid;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IClinicalNoteSuccessData>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return safeCombineLatest(
      record.data.clinicalNoteRefs.map((clinicalNoteRef) =>
        Firestore.doc$(clinicalNoteRef)
      )
    ).pipe(
      map((clinicalNotes) => [
        ...clinicalNotes.map((clinicalNote) => ({
          label: 'Clinical Notes',
          data: clinicalNote,
        })),
      ])
    );
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IClinicalNoteMigrationData
  ): Promise<IClinicalNoteMigrationData | undefined> {
    const existingClinicalNotes: (IClinicalNote & IHasSourceIdentifier)[] = [];

    const clinicalNoteMergeConflicts = await resolveSequentially(
      data.clinicalNotes,
      async (note) => {
        const noteRef = await translationMap.getDestination<IClinicalNote>(
          note.sourceIdentifier,
          PATIENT_CLINICAL_NOTE_CUSTOM_MAPPING_TYPE
        );
        if (!noteRef) {
          return;
        }

        const clinicalNote = await Firestore.getDoc(noteRef);
        existingClinicalNotes.push({
          ...clinicalNote,
          sourceIdentifier: note.sourceIdentifier,
        });

        return hasMergeConflicts(note, clinicalNote);
      }
    );

    if (clinicalNoteMergeConflicts.some((conflict) => conflict)) {
      return {
        ...data,
        clinicalNotes: existingClinicalNotes,
      };
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: JobData,
    _migrationData: IClinicalNoteMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: JobData,
    migrationData: IClinicalNoteMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const clinicalNoteRefs = await runTransaction(async (transaction) =>
        this._upsertClinicalNotes(
          migrationData.clinicalNotes,
          translationMap,
          migrationData.patientRef,
          transaction
        )
      );

      return this._buildSuccessResponse(
        jobData.sourcePatient,
        clinicalNoteRefs
      );
    } catch (error) {
      return this._buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  buildSkippedResponse(
    data: JobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  protected _buildSuccessResponse(
    patient: IGetRecordResponse<PatientRecord>,
    clinicalNoteRefs: DocumentReference<IClinicalNote>[]
  ): IDestinationEntityRecord<IClinicalNoteSuccessData> {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      data: {
        sourceRef: patient.record.id,
        clinicalNoteRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.record.id,
      migratedAt: toTimestamp(),
    };
  }

  protected _buildErrorResponse(
    patient: Pick<
      IGetRecordResponse<PatientRecord>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage?: string
  ): IDestinationEntityRecord<IClinicalNoteSuccessData> &
    FailedDestinationEntityRecord {
    return {
      uid: patient.uid,
      label: patient.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.id,
      errorMessage: errorMessage ?? `Can't resolve data to generate charts`,
      failData: {
        patientRef: patient.id,
      },
    };
  }
  private async _upsertClinicalNotes(
    clinicalNotes: (IClinicalNote & IHasSourceIdentifier)[],
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>,
    transaction: Transaction
  ): Promise<DocumentReference<IClinicalNote>[]> {
    return resolveSequentially(clinicalNotes, async (clinicalNote) => {
      const noteDestinationRef = await translationMap.getDestination(
        clinicalNote.sourceIdentifier,
        PATIENT_CLINICAL_NOTE_CUSTOM_MAPPING_TYPE
      );

      const noteRef = await FirestoreMigrate.upsertDoc(
        ClinicalNote.col({
          ref: patientRef,
        }),
        clinicalNote,
        noteDestinationRef?.id,
        transaction
      );

      if (!noteDestinationRef) {
        await translationMap.upsert({
          sourceIdentifier: clinicalNote.sourceIdentifier,
          destinationIdentifier: noteRef,
          resourceType: PATIENT_CLINICAL_NOTE_CUSTOM_MAPPING_TYPE,
        });
      }
      return noteRef;
    });
  }
}
